/** Bildirishnomalar: o'qilmaganlar ajratilgan, bosilsa o'qilgan deb belgilanadi va havolaga o'tadi. */
import { usePageSize } from "@/lib/pageSize";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ArrowRightLeft, Bell, Check, Info, type LucideIcon } from "lucide-react";
import { ApiError } from "@/lib/api";
import { cn, dayjs, formatWhen } from "@/lib/utils";
import { useMarkAllRead, useMarkRead, useMarkUnread, useNotifications, type Notification } from "@/features/notifications/api";
import { notificationMenu } from "@/features/notifications/menu";
import { ContextMenu } from "@/components/ui/context-menu";
import { copyText, openInNewTab } from "@/lib/clipboard";
import { Button } from "@/components/ui/button";
import { EmptyState, ErrorState, PageHeader, PageLoading } from "@/components/ui/misc";
import { FilterChips, Pagination } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";

type Filter = "all" | "unread";

function iconFor(kind: string): { Icon: LucideIcon; className: string } {
  if (kind.startsWith("approval_") || kind.endsWith("_approved")) return { Icon: Check, className: "bg-success-soft text-success" };
  if (kind.endsWith("_rejected")) return { Icon: Info, className: "bg-danger-soft text-danger" };
  if (kind === "driver_transferred") return { Icon: ArrowRightLeft, className: "bg-primary-soft text-primary" };
  if (kind.startsWith("reminder")) return { Icon: Bell, className: "bg-warning-soft text-warning" };
  return { Icon: Info, className: "bg-fill text-ink-soft" };
}

export function NotificationsPage() {
  const { t } = useTranslation("notifications");
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize("notifications");
  const list = useNotifications(filter === "unread", page, pageSize);
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();
  const markUnread = useMarkUnread();
  const { t: tm } = useTranslation("menu");

  function open(n: Notification) {
    if (!n.read_at) markRead.mutate(n.id);
    if (n.link) navigate(n.link);
  }

  function markAllRead() {
    markAll
      .mutateAsync()
      .then((r) => toast.success(r.updated ? t("marked", { count: r.updated }) : t("nothingToMark")))
      .catch((e) => toast.error(e instanceof ApiError ? e.message : t("markFailed")));
  }

  const fail = (e: unknown) => toast.error(e instanceof ApiError ? e.message : t("markFailed"));
  const menuFor = (n: Notification) =>
    notificationMenu(
      n,
      {
        open: () => open(n),
        openNewTab: () => {
          if (!n.read_at) markRead.mutate(n.id);
          openInNewTab(n.link);
        },
        markRead: () => markRead.mutate(n.id, { onError: fail }),
        markUnread: () => markUnread.mutate(n.id, { onError: fail }),
        markAllRead,
        copy: (text) => void copyText(text),
      },
      tm,
    );

  const unreadOnPage = (list.data?.results ?? []).filter((n) => !n.read_at).length;

  return (
    <div className="mx-auto max-w-[860px]">
      <PageHeader
        title={t("title")}
        subtitle={list.data ? t("total", { count: list.data.count }) : undefined}
        actions={
          <Button
            loading={markAll.isPending}
            onClick={markAllRead}
          >
            {t("markAll")}
          </Button>
        }
      >
        <div className="mt-3">
          <FilterChips<Filter>
            items={[
              { key: "all", label: t("common:states.all") },
              { key: "unread", label: t("unread"), count: filter === "unread" ? list.data?.count : unreadOnPage || undefined },
            ]}
            value={filter}
            onChange={(f) => {
              setFilter(f);
              setPage(1);
            }}
          />
        </div>
      </PageHeader>

      {list.isError ? (
        <ErrorState message={list.error instanceof ApiError ? list.error.message : t("loadFailed")} onRetry={() => list.refetch()} />
      ) : list.isPending ? (
        <PageLoading />
      ) : list.data.results.length === 0 ? (
        <EmptyState title={filter === "unread" ? t("caughtUp") : t("none")} description={t("desc")} />
      ) : (
        <div className="divide-y divide-line rounded-card border border-border bg-surface">
          {list.data.results.map((n) => {
            const { Icon, className } = iconFor(n.kind);
            const unread = !n.read_at;
            return (
              <ContextMenu key={n.id} items={() => menuFor(n)}>
              <button
                type="button"
                onClick={() => open(n)}
                className={cn("flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-fill/60 data-[state=open]:bg-primary-soft/50", unread && "bg-primary-soft/20")}
              >
                <span className={cn("mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full", className)}>
                  <Icon size={15} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    {unread && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-label={t("unread")} />}
                    <span className={cn("truncate text-[13px]", unread ? "font-semibold" : "font-medium")}>{n.title}</span>
                  </span>
                  {n.body && <span className="mt-0.5 block text-[12px] text-ink-soft">{n.body}</span>}
                </span>
                <span className="shrink-0 text-[11px] text-muted" title={formatWhen(n.created_at)}>
                  {dayjs(n.created_at).fromNow()}
                </span>
              </button>
              </ContextMenu>
            );
          })}
        </div>
      )}

      {list.data && list.data.pages > 1 && (
        <Pagination page={page} pages={list.data.pages} count={list.data.count} pageSize={list.data.page_size}
          onPage={setPage}
          onPageSize={(s) => {
            setPageSize(s);
            setPage(1);
          }} label={t("pagination")} />
      )}
    </div>
  );
}
